import { hideSlots } from '../../utils/storeSafe';
import React, { useState, useEffect } from 'react';
import { HStack, Box, Icon, VStack, Text } from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Home, Gamepad2, Wallet, Trophy, GraduationCap, User, MessageCircle, Dices } from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';
import { auth } from '../../services/firebase';
import { subscribeToUnreadCount } from '../../services/messageService';
import { subscribeToPendingInvites } from '../../services/inviteService';

const BottomNav: React.FC = () => {
  const location = useLocation();
  const [notificationCount, setNotificationCount] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);
  
  // Track auth state
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUserId(user?.uid || null);
    });
    return () => unsubscribe();
  }, []);
  
  // Subscribe to unread DMs and pending invites
  useEffect(() => {
    if (!userId) return;

    let unreadDMs = 0;
    let pendingInvites = 0;

    const updateCount = () => {
      setNotificationCount(unreadDMs + pendingInvites);
    };

    const unsubDMs = subscribeToUnreadCount(userId, (count) => {
      unreadDMs = count;
      updateCount();
    });

    const unsubInvites = subscribeToPendingInvites(userId, (invites) => {
      pendingInvites = invites.length;
      updateCount();
    });

    return () => {
      unsubDMs();
      unsubInvites();
    };
  }, [userId]);
  
  // 8 items for mobile - Admin/Agent links are in Profile page instead
  const navItems = [
    { icon: Home, label: 'Home', to: '/' },
    { icon: Gamepad2, label: 'Tables', to: '/tables' },
    { icon: GraduationCap, label: 'Training', to: '/training', highlight: true },
    { icon: Trophy, label: 'Tourneys', to: '/tournaments' },
    { icon: Wallet, label: 'Wallet', to: '/wallet' },
    ...(hideSlots() ? [] : [{ icon: Dices, label: 'Games', to: '/slots' }]),
    { icon: MessageCircle, label: 'Messages', to: '/messages' },
    { icon: User, label: 'Profile', to: '/profile' },
  ];

  const isActive = (path: string) => {
    if (path === '/training') {
      return location.pathname === '/training' || location.pathname.startsWith('/training/');
    }
    if (path === '/slots') {
      return location.pathname === '/slots' || location.pathname.startsWith('/slots/');
    }
    if (path === '/profile') {
      return location.pathname === '/profile';
    }
    if (path === '/messages') {
      return location.pathname === '/messages';
    }
    return location.pathname === path;
  };

  return (
    <Box
      as="nav"
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      bg={POKER_COLORS.darkBg}
      borderTop={`1px solid ${POKER_COLORS.cyan}33`}
      py={2}
      //  MOBILE: Add safe area padding for iOS home indicator

      pb="calc(12px + env(safe-area-inset-bottom, 0px))"
      display={{ base: 'flex', md: 'none' }}
      zIndex={100}
    >
      {/* EIGHT items have to share a phone's width. Left to their natural
          widths they overflowed and the last one - Profile - was pushed off
          the screen entirely, which is why it looked like it was missing.
          Each item now takes an equal share and cannot grow past it. */}
      <HStack
        justify="space-between"
        w="100%"
        px={0.5}
        spacing={0}
        /* ...and if they still cannot all fit - a very narrow phone, or more
           items added later - the bar SCROLLS sideways rather than hiding
           the overflow, so nothing is ever unreachable. Scrollbar hidden,
           momentum scrolling on iOS. */
        overflowX="auto"
        overflowY="hidden"
        sx={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {navItems.map((item) => (
          <RouterLink
            key={item.label}
            to={item.to}
            style={{ flex: '1 1 0', minWidth: '44px' }}
          >
            <VStack
              spacing={0.5}
              cursor="pointer"
              transition="all 0.2s"
              w="100%"
              px={0}
              py={1.5}
              borderRadius="lg"
              bg={item.highlight && isActive(item.to) ? `${POKER_COLORS.gold}22` : 'transparent'}
              border={item.highlight && isActive(item.to) ? `1px solid ${POKER_COLORS.gold}44` : 'none'}
              _hover={{
                transform: 'scale(1.1)',
              }}
            >
              <Box position="relative">
                <Icon
                  as={item.icon}
                  w={5}
                  h={5}
                  color={
                    item.highlight && isActive(item.to)
                      ? POKER_COLORS.gold 
                      : isActive(item.to) 
                        ? POKER_COLORS.cyan 
                        : 'gray.500'
                  }
                />
                {/* Notification badge for Messages */}
                {item.to === '/messages' && notificationCount > 0 && (
                  <Box
                    position="absolute"
                    top="-4px"
                    right="-6px"
                    bg="red.500"
                    color="white"
                    fontSize="2xs"
                    fontWeight="bold"
                    borderRadius="full"
                    minW="14px"
                    h="14px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    px={1}
                  >
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </Box>
                )}
              </Box>
              <Text
                fontSize="9px"
                whiteSpace="nowrap"
                lineHeight="1.1"
                maxW="100%"
                overflow="hidden"
                textOverflow="ellipsis"
                textAlign="center" 
                color={
                  item.highlight && isActive(item.to)
                    ? POKER_COLORS.gold 
                    : isActive(item.to) 
                      ? POKER_COLORS.cyan 
                      : 'gray.500'
                }
                fontWeight={isActive(item.to) ? 'bold' : 'normal'}
              >
                {item.label}
              </Text>
            </VStack>
          </RouterLink>
        ))}
      </HStack>
    </Box>
  );
};

export default BottomNav;